"use client";

type LandingSignInButtonProps = {
  busyLabel: string;
  isBusy: boolean;
  label: string;
  onClick: () => void;
  variant: "desktop" | "mobile";
};

export default function LandingSignInButton({
  busyLabel,
  isBusy,
  label,
  onClick,
  variant,
}: LandingSignInButtonProps) {
  // A floor, not a fixed width: a longer label ("Commencer gratuitement")
  // widens the button instead of wrapping inside it.
  const dimensions = variant === "mobile"
    ? "h-[54px] min-w-[176px] px-7 text-[16px]"
    : "h-[46px] min-w-[164px] px-6 text-[14px]";

  return (
    <>
      <button
        type="button"
        aria-busy={isBusy}
        disabled={isBusy}
        onClick={onClick}
        className={`landingSignInButton relative isolate inline-flex shrink-0 cursor-pointer whitespace-nowrap items-center justify-center bg-transparent font-medium tracking-[0.01em] text-[#00E6EE] transition-[color,filter,opacity,transform] duration-200 hover:text-cyan-50 active:scale-[0.97] disabled:cursor-wait disabled:opacity-55 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200 motion-reduce:transition-none ${dimensions}`}
      >
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          preserveAspectRatio="none"
          viewBox="0 0 196 54"
        >
          <path
            className="landingSignInBorder"
            d="M1 19L19 1H177L195 19V35L177 53H19L1 35Z"
            fill="rgba(0,230,238,0.018)"
            pathLength="452"
            stroke="#00E6EE"
            strokeDasharray="113 113"
            strokeLinecap="square"
            strokeWidth="1.35"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        <span aria-live="polite" className="relative z-10">
          {isBusy ? busyLabel : label}
        </span>
      </button>

      <style jsx>{`
        .landingSignInButton {
          filter: drop-shadow(0 0 9px rgba(0, 230, 238, 0.16));
        }

        .landingSignInBorder {
          animation: landing-sign-in-border-travel 8.4s cubic-bezier(0.65, 0, 0.35, 1) infinite;
          transition: filter 200ms ease, stroke-width 200ms ease;
        }

        .landingSignInButton:hover .landingSignInBorder,
        .landingSignInButton:focus-visible .landingSignInBorder {
          filter: drop-shadow(0 0 5px rgba(0, 230, 238, 0.7));
          stroke-width: 1.65;
        }

        @keyframes landing-sign-in-border-travel {
          0%,
          22.62% {
            stroke-dashoffset: 0;
          }
          50%,
          72.62% {
            stroke-dashoffset: -113;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .landingSignInBorder {
            animation: none;
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </>
  );
}
